import { useState } from 'react'

import { DatePicker } from '@/components/common/DatePicker'
import { toast } from '@/components/common/Toast'
import { Icon } from '@/components/icons'
import { Button, Input, Label, Textarea } from '@/components/ui'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { BILL_LOCATION_LABELS, BILL_LOCATION_VALUES, type BillLocationValue } from '@/constants/bill-location'
import { useTranslations } from '@/hooks/useTranslations'
import { cn } from '@/lib/utils'
import {
  useDeleteTempWorkSession,
  useUpdateTempWorkSession,
  type TempWorkSession,
} from '@/modules/temp-worker/services/tempWorker'
import { isTempWorkSessionEditable } from '@/components/pages/Admin/TempWorkers/tempWorkSessionEditable'

type Props = {
  session: TempWorkSession
  paidThroughWorkDate?: string | null
  namespace?: 'temp_workers' | 'temp_worker_detail'
  compact?: boolean
  onSuccess?: (kind: 'update' | 'delete', next: TempWorkSession) => void
}

function ymdToDate(ymd: string): Date | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd.trim())
  if (!m) return undefined
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0, 0)
}

function dateToYmd(d: Date): string {
  const y = d.getFullYear()
  const mo = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${mo}-${day}`
}

export function TempWorkSessionRowActions({
  session,
  paidThroughWorkDate,
  namespace = 'temp_workers',
  compact = false,
  onSuccess,
}: Props) {
  const tTempWorkers = useTranslations('temp_workers')
  const tDetail = useTranslations('temp_worker_detail')
  const t = namespace === 'temp_worker_detail' ? tDetail : tTempWorkers
  const tButtons = useTranslations('buttons')

  const $update = useUpdateTempWorkSession()
  const $delete = useDeleteTempWorkSession()

  const isClosed = session.check_out_at != null
  const isEditable = isTempWorkSessionEditable(session, paidThroughWorkDate)
  const isPaidLocked = isClosed && !isEditable

  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)

  const [draftWorkDate, setDraftWorkDate] = useState(() => session.work_date)
  const [draftPay, setDraftPay] = useState(() => String(session.pay_baht ?? ''))
  const [draftLocation, setDraftLocation] = useState(() => (session.location ? session.location : '__none__'))
  const [draftNote, setDraftNote] = useState(() => session.note ?? '')

  const resetDraft = () => {
    setDraftWorkDate(session.work_date)
    setDraftPay(String(session.pay_baht ?? ''))
    setDraftLocation(session.location ? session.location : '__none__')
    setDraftNote(session.note ?? '')
  }

  const openEdit = () => {
    if (!isEditable) return
    resetDraft()
    setEditOpen(true)
  }

  const onSubmitEdit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isEditable) return
    const n = parseFloat(draftPay)
    if (!Number.isFinite(n) || n < 0) {
      toast.toastFailed(t('invalid_pay'))
      return
    }
    try {
      const updated = await $update.mutateAsync({
        id: session.id,
        work_date: draftWorkDate,
        pay_baht: n,
        location: draftLocation === '__none__' ? null : (draftLocation as BillLocationValue),
        note: draftNote.trim() || null,
      })
      toast.toastSuccess(t('update_session_ok'))
      setEditOpen(false)
      onSuccess?.('update', updated.data)
    } catch (err: any) {
      toast.toastFailed(
        err?.response?.data?.message || err?.message || t('update_session_fail'),
      )
    }
  }

  const onConfirmDelete = async () => {
    try {
      const deleted = await $delete.mutateAsync({ id: session.id })
      toast.toastSuccess(t('delete_session_ok'))
      onSuccess?.('delete', deleted.data ?? session)
      setDeleteOpen(false)
    } catch (err: any) {
      toast.toastFailed(
        err?.response?.data?.message || err?.message || t('delete_session_fail'),
      )
    }
  }

  if (!isClosed) return null
  if (isPaidLocked) {
    return (
      <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-800 dark:bg-slate-900 dark:text-slate-200">
        {t('locked_paid')}
      </span>
    )
  }

  return (
    <>
      <div className={cn('flex items-center gap-2', compact && 'gap-1')}>
        <Button
          type="button"
          variant="outline"
          size={compact ? 'sm' : 'sm'}
          disabled={$update.isLoading || $delete.isLoading}
          onClick={openEdit}
          className={compact ? 'w-8 px-0' : undefined}
        >
          <Icon icon="mdi:pencil" className={compact ? 'mx-auto size-4' : 'mr-2 size-4'} />
          {!compact ? tButtons('edit') : null}
        </Button>
        <Button
          type="button"
          variant="destructive"
          size={compact ? 'sm' : 'sm'}
          disabled={$update.isLoading || $delete.isLoading}
          onClick={() => setDeleteOpen(true)}
          className={compact ? 'w-8 px-0' : undefined}
        >
          <Icon icon="mdi:trash-can-outline" className={compact ? 'mx-auto size-4' : 'mr-2 size-4'} />
          {!compact ? t('delete_session_btn') : null}
        </Button>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('edit_session_title')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={onSubmitEdit} className="space-y-4">
            <div>
              <Label className="text-sm">{t('edit_session_work_date')}</Label>
              <DatePicker
                className="mt-1.5 h-10 w-full"
                value={ymdToDate(draftWorkDate) ?? undefined}
                onChange={(d) => {
                  if (!d) return
                  setDraftWorkDate(dateToYmd(d))
                }}
              />
            </div>
            <div>
              <Label className="text-sm">{t('edit_session_pay')}</Label>
              <Input
                type="number"
                min={0}
                step="0.01"
                className="mt-1.5"
                value={draftPay}
                onChange={(e) => setDraftPay(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-sm">{t('edit_session_location')}</Label>
              <Select
                value={draftLocation}
                onValueChange={(v) => setDraftLocation(v)}
              >
                <SelectTrigger className="mt-1.5 h-10">
                  <SelectValue placeholder={t('optional')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{t('optional')}</SelectItem>
                  {BILL_LOCATION_VALUES.map((loc) => (
                    <SelectItem key={loc} value={loc}>
                      {BILL_LOCATION_LABELS[loc]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">{t('note_label')}</Label>
              <Textarea
                className="mt-1.5 min-h-[88px] resize-y"
                value={draftNote}
                onChange={(e) => setDraftNote(e.target.value)}
                placeholder={t('note_placeholder')}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>
                {tButtons('cancel')}
              </Button>
              <Button type="submit" disabled={$update.isLoading}>
                {tButtons('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('delete_session_title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('delete_session_confirm')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tButtons('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(ev) => {
                ev.preventDefault()
                void onConfirmDelete()
              }}
              disabled={$delete.isLoading}
            >
              {tButtons('confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

